import { useState } from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PricingPlans() {
    const navigate = useNavigate();
    const [expandedCards, setExpandedCards] = useState<number[]>([]);

    const toggleExpand = (index: number) => {
        setExpandedCards(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const plans = [
        {
            tag: "For Getting Started",
            title: "Standard",
            description: "Essential Communication for Small Businesses",
            monthlyPrice: "$2.23",
            yearlyPrice: "$1.99",
            isPaid: true,
            popular: false,
            features: [
                "VoIP service Phone System Setup (up to 10 users)",
                "Basic call routing and voicemail",
                "Web conferencing (up to 5 participants)",
                "Integration with mobile and desktop apps",
                "On-premise or cloud deployment options"
            ],
            additional_info: [
                "Cost-effective and easy to set up",
                "Reliable communication with HD voice quality",
                "Access to VoIP service apps for mobile and desktop"
            ],
            ideal_for: "Ideal For: Small businesses or startups looking for an affordable VoIP service solution."
        },
        {
            tag: "For boosting productivity",
            title: "Professional",
            description: "Enhanced Features for Growing Teams",
            monthlyPrice: "$7.99",
            yearlyPrice: "$6.99",
            isPaid: false,
            popular: true,
            features: [
                "VoIP service Phone System Setup (up to 50 users)",
                "Advanced call routing and automatic attendants",
                "Web conferencing (up to 10 participants)",
                "Integration with CRM systems like Salesforce, HubSpot, etc.",
                "Call center functionality (basic)"
            ],
            additional_info: [
                "Scalable as your business grows",
                "Improved productivity with integrations",
                "Enhanced team collaboration and reporting tools"
            ],
            ideal_for: "Ideal For: Growing businesses that need advanced communication tools for better customer support and internal collaboration."
        },
        {
            tag: "For collaboration & flexibility",
            title: "Enterprise",
            description: "Full-Featured Communication Solution for Large Businesses",
            monthlyPrice: "$2.23",
            yearlyPrice: "$1.99",
            isPaid: false,
            popular: false,
            features: [
                "VoIP service Phone System Setup (unlimited users)",
                "Enterprise-grade call center features",
                "Advanced web conferencing (unlimited participants)",
                "Full CRM integration and custom APIs",
                "Dedicated account manager and priority support"
            ],
            additional_info: [
                "Full enterprise-grade communication system",
                "Tailored to fit your unique business needs",
                "24/7 premium support and cloud hosting options"
            ],
            ideal_for: "Ideal For: Large enterprises or companies that need a robust, scalable, and secure communication platform."
        }
    ];

    return (
        <div className="container py-16 lg:pt-16 lg:pb-24" id="pricing-voip">
            {/* Header */}
            <div className="text-center mb-16">
                <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/5 rounded-full border border-white/10 backdrop-blur-sm text-xs md:text-sm font-semibold text-gray-300 tracking-wider uppercase">
                    Pricing Plans
                </span>
                <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
                    VoIP Service Packages
                </h3>
                <p className="text-gray-300 md:text-lg max-w-3xl mx-auto">
                    We offer comprehensive VoIP service services to ensure that your business is equipped with the latest in communication technology, tailored to your specific needs.
                </p>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto items-stretch">
                {plans.map((plan, index) => {
                    const isExpanded = expandedCards.includes(index);
                    
                    return (
                        <div
                            key={index}
                            className="relative h-full"
                        >
                            {/* Popular Badge */}
                            {plan.popular && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                                    <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wide">
                                        Most Popular
                                    </div>
                                </div>
                            )}

                            <div
                                className={`h-full flex flex-col bg-gradient-to-br from-purple-600/5 to-blue-600/5 backdrop-blur-xl rounded-2xl p-8 border ${
                                    plan.popular ? 'border-purple-500/30' : 'border-purple-500/20'
                                } hover:border-purple-500/50 transition-all duration-300 shadow-xl`}
                            >
                                {/* Title */}
                                <h2 className="text-white text-2xl font-bold mb-2">
                                    {plan.title}
                                </h2>

                                {/* Tag */}
                                <p className="text-sm font-medium mb-4 uppercase tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                                    {plan.tag}
                                </p>

                                {/* Description */}
                                <p className="text-gray-300 text-sm leading-relaxed mb-5 min-h-[50px]">
                                    {plan.description}
                                </p>

                                {/* CTA Button */}
                                {plan.isPaid ? (
                                    <button
                                        onClick={() => navigate('/contact-us')}
                                        className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-semibold py-3.5 px-6 rounded-lg transition-all duration-300 mb-8 shadow-md hover:shadow-lg active:scale-[0.98]"
                                    >
                                        Buy Now
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => navigate('/contact-us')}
                                        className="w-full bg-transparent border-2 border-purple-500/30 hover:border-purple-500/50 hover:bg-purple-500/10 text-white font-semibold py-3.5 px-6 rounded-lg transition-all duration-300 mb-8"
                                    >
                                        Talk to Sales
                                    </button>
                                )}

                                {/* Divider */}
                                <div className="h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent mb-8" />

                                {/* Features */}
                                <div className="flex-grow mb-6">
                                    <h3 className="text-white font-bold mb-4 text-base">Features:</h3>
                                    <ul className="space-y-3">
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center mt-0.5">
                                                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                                </div>
                                                <span className="text-gray-300 text-sm leading-relaxed">
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* More Details Accordion */}
                                <div className="border-t border-purple-500/20 pt-6 mt-auto">
                                    <button
                                        onClick={() => toggleExpand(index)}
                                        className="w-full flex items-center justify-between text-white font-bold text-base hover:text-purple-400 transition-colors duration-200 group"
                                    >
                                        <span>More Details</span>
                                        <div className="transition-transform duration-300 ease-in-out group-hover:scale-110">
                                            {isExpanded ? (
                                                <ChevronUp className="w-5 h-5" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5" />
                                            )}
                                        </div>
                                    </button>

                                    {/* Expandable Content with Smooth Animation */}
                                    <div
                                        className={`grid transition-all duration-500 ease-in-out ${
                                            isExpanded 
                                                ? 'grid-rows-[1fr] opacity-100 mt-4' 
                                                : 'grid-rows-[0fr] opacity-0'
                                        }`}
                                    >
                                        <div className="overflow-hidden">
                                            <div className="space-y-6 pt-2">
                                                {/* Why choose this package */}
                                                <div>
                                                    <h4 className="text-white font-semibold mb-3 text-sm">
                                                        Why choose this package:
                                                    </h4>
                                                    <ul className="space-y-3">
                                                        {plan.additional_info.map((info, idx) => (
                                                            <li key={idx} className="flex items-start gap-3">
                                                                <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center mt-0.5">
                                                                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                                                                </div>
                                                                <span className="text-gray-300 text-sm leading-relaxed">
                                                                    {info}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>

                                                {/* Ideal For */}
                                                <div className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 rounded-lg p-4 border border-purple-500/20">
                                                    <p className="text-gray-300 text-sm leading-relaxed">
                                                        {plan.ideal_for}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}